import { Bomb, Zap, PanelLeft, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { openSupport, useSupportAvailable } from '@/lib/freshdesk';

/**
 * Prompt 1 / 6 / 7: the clock strip.
 *
 * This is where the design's biggest structural change lands. Today the build carries a
 * 180px "DRAFT PAUSED" hero banner and a 160px "ON THE CLOCK" card as page-level regions.
 * Both collapse into here, because draft state is a property of the clock -- the tile colour
 * and one status line say everything those two blocks said, in 64px instead of 340px.
 *
 * TOKENS. The handoff's token list does not match this codebase: it names --bg-elev-1,
 * --bg-muted, --fg-muted, --fg-subtle and --border-muted, none of which exist in index.css.
 * Rather than introduce new CSS variables (the README explicitly says not to), every surface
 * here maps onto the tokens that DO exist -- card / muted / muted-foreground / border. The
 * intent survives; the variable names do not.
 */

/**
 * `idle` covers pending / completed / cancelled: no clock is running and NOBODY is on the
 * clock. Without it those states fall through to `running`, whose status word is "ON CLOCK"
 * -- so a finished draft displayed "COMPLETED" and "ON CLOCK" simultaneously.
 */
export type ClockState = 'running' | 'urgent' | 'paused' | 'yours' | 'idle';

export interface ClockStripProps {
  /** Seconds remaining, or null when there is no live clock. */
  timeLeft: number | null;
  state: ClockState;
  /** "Dress Bot 07 is on the clock" / "Your pick". */
  headline: string;
  /** "Round 2 · Pick 12 of 190 · Snake ←". */
  subline: string;
  /** Eyebrow copy: "LIVE", "PAUSED BY COMMISSIONER". */
  eyebrow: string;
  /**
   * Cards held, or null when unknown.
   *
   * The design disables this button at zero. That needs a real count, and there is no
   * source for one on this page: useDraftSabotageEffects returns activeEffects/cardMap/
   * refetchActive, and NFLDraftSabotagePanel fetches nfl_sabotage_cards itself when it
   * opens. Passing a hardcoded 0 would disable the button permanently, which is exactly
   * what happened before this was caught. null therefore means "unknown" -- no badge, and
   * NOT disabled, matching the live room, which has no count and no gate at all.
   */
  sabotageCount: number | null;
  onSabotageClick: () => void;
  /** The pick ticker. Passed as a node so this file owns no ticker data logic. */
  ticker?: React.ReactNode;
  /** 1024-1180px: the roster rail collapses behind this toggle. Still UNWIRED -- no
   *  caller passes it, so the button below never renders. Tracked as SAB-874. */
  onToggleRail?: () => void;
  railCollapsed?: boolean;
}

function mmss(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '--:--';
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Border / fill / text move together, per the design's state table. Kept as one lookup so a
 * new state cannot colour the border without colouring the text.
 */
const TILE: Record<ClockState, string> = {
  running: 'border-primary bg-primary/10 text-primary',
  urgent: 'border-damage bg-damage/10 text-damage animate-pulse-glow',
  paused: 'border-gold bg-gold/10 text-gold',
  yours: 'border-primary bg-primary/10 text-primary shadow-[var(--shadow-sabotage)]',
  idle: 'border-border bg-muted text-muted-foreground',
};

const STATUS_WORD: Record<ClockState, string> = {
  running: 'ON CLOCK',
  urgent: 'HURRY',
  paused: 'PAUSED',
  yours: 'YOUR PICK',
  idle: '—',
};

const DOT: Record<ClockState, string> = {
  running: 'bg-success',
  urgent: 'bg-damage',
  paused: 'bg-gold',
  yours: 'bg-primary',
  idle: 'bg-muted-foreground',
};

export default function ClockStrip({
  timeLeft, state, headline, subline, eyebrow,
  sabotageCount, onSabotageClick, ticker,
  onToggleRail, railCollapsed,
}: ClockStripProps) {
  const supportAvailable = useSupportAvailable();

  return (
    <div
      data-testid="nfl-clock-strip"
      className="flex-none flex items-center gap-5 border-b border-border px-[18px] py-3 bg-gradient-to-r from-primary/10 to-transparent"
    >
      {/* 1024-1180px only: the rail toggle lives in the clock strip, per the responsive rule.
          SAB-871 moved the lower bound from 900 to 1024 so it matches the mobile switch --
          below 1024 MobileDraftRoom renders and this strip is not on screen at all. */}
      {onToggleRail && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={railCollapsed ? 'Show roster rail' : 'Hide roster rail'}
          aria-pressed={!railCollapsed}
          onClick={onToggleRail}
          className="hidden max-[1180px]:inline-flex min-[1024px]:inline-flex h-11 w-11 shrink-0"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      )}

      <div
        data-testid="clock-tile"
        data-state={state}
        className={cn(
          'flex-none h-16 w-16 rounded-xl border-2 flex flex-col items-center justify-center',
          TILE[state],
        )}
      >
        <span className="font-display font-extrabold text-[26px] leading-none tabular-nums">
          {mmss(timeLeft)}
        </span>
        <span className="font-display font-bold text-[8px] tracking-[0.1em] mt-1">
          {STATUS_WORD[state]}
        </span>
      </div>

      <div className="h-[52px] w-px flex-none bg-border" />

      <div className="flex flex-col gap-[3px] min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('h-[7px] w-[7px] rounded-full animate-pulse', DOT[state])} />
          <span className="font-display font-bold text-[10px] tracking-[0.1em] text-muted-foreground uppercase">
            {eyebrow}
          </span>
        </div>
        <div className="font-display font-extrabold text-[19px] text-white truncate">{headline}</div>
        <div className="text-xs text-muted-foreground truncate">{subline}</div>
      </div>

      {/* min-w-0 is what lets the ticker actually clip and scroll instead of stretching the
          row. Without it this flex child sizes to content and pushes the sabotage button
          off-screen -- the flex counterpart of the grid min-height:0 trap. */}
      <div className="flex-1 min-w-0 overflow-hidden">{ticker}</div>

      {/* SAB-943: the desktop room's support route.
          The floating launcher overlays the scrolling right rail here (SAB-916, still open),
          so a commissioner mid-draft should not have to hunt for it. The clock strip is
          chrome, not a scroll region, so this control cannot cover rail content.
          ABSENT, NOT INERT (SAB-790): rendered only once the widget reports loaded, because
          consent alone leaves a window where the vendor script is still in flight and a tap
          would do nothing. */}
      {supportAvailable && (
        <Button
          data-testid="desktop-support"
          variant="outline"
          onClick={() => openSupport()}
          aria-label="Contact support"
          className="flex-none h-10 w-10 p-0 rounded-[10px] border-border text-muted-foreground hover:text-foreground"
        >
          <LifeBuoy className="h-4 w-4" />
        </Button>
      )}

      <Button
        data-testid="sabotage-button"
        onClick={onSabotageClick}
        disabled={sabotageCount !== null && sabotageCount <= 0}
        className={cn(
          'flex-none h-10 px-4 rounded-[10px] gap-2 font-display font-extrabold text-[13px] text-white',
          'bg-[image:var(--gradient-damage)] hover:opacity-90',
          sabotageCount === null || sabotageCount > 0 ? 'shadow-[var(--shadow-damage)]' : 'opacity-40 shadow-none',
        )}
      >
        <Bomb className="h-4 w-4" />
        Sabotage
        {sabotageCount !== null && (
          <span className="ml-1 rounded-full bg-black/35 px-2 py-0.5 text-[10px] font-bold tabular-nums">
            {sabotageCount}
          </span>
        )}
      </Button>
    </div>
  );
}

/** The `⚡AUTO` chip, one definition shared by the ticker, board header and feed rows. */
export function AutoChip({ className }: { className?: string }) {
  return (
    <span
      data-testid="auto-chip"
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1.5 py-px',
        'bg-[hsl(220_90%_56%/0.18)] text-[#7EB0F7]',
        'font-display font-bold text-[8px] tracking-[0.04em]',
        className,
      )}
    >
      <Zap className="h-2.5 w-2.5" />
      AUTO
    </span>
  );
}
